import {useCallback, useEffect, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Loader2} from '@digital-twins/design-system/icons'
import {resolvePlannedSampleSize, type QuestionnaireThreadDraftListItem, type QuickStudyQuote} from '@digital-twins/shared-types'
import {Button} from '@/components/ui/button'
import {Progress} from '@/components/ui/progress'
import {CreditIcon} from '@/components/icons/CreditIcon'
import {STUDY_EMBED_SOURCE, studyQuestionsVisibleInParent} from '@/features/studies/embed/studyEmbedBridge'
import {getQuestionnaireDraftApi, launchQuestionnaireDraftApi, quoteQuickStudyDraftApi} from '@/features/studies/questionnaire/api/questionnaireDraftsApi'
import {studyEmbedDraftPath} from '@/routes/routes'

interface Props {
  draft: QuestionnaireThreadDraftListItem
  onLaunched: (studyToken: string) => Promise<unknown> | void
  onOpenResults: (studyToken: string) => void
  onQuestionsDisplayed?: (draftToken: string, revision: number) => void
  questionAttentionKey?: string
  findingsReady?: boolean
  readOnly?: boolean
}

function requestError(error: unknown, otherwise: string): string {
  const response = (error as {response?: {status?: number; data?: {message?: unknown}}})?.response
  return response?.status && response.status >= 400 && response.status < 500 && typeof response.data?.message === 'string'
    ? response.data.message : otherwise
}

/** One canonical draft editor and its approval, pinned to its saved user origin. */
export function ThreadStudyCard({draft, onLaunched, onOpenResults, findingsReady = false, readOnly = false, onQuestionsDisplayed, questionAttentionKey}: Props) {
  const {t} = useTranslation('app')
  const frame = useRef<HTMLIFrameElement | null>(null)
  const questionsViewport = useRef<unknown>(null)
  const visible = useRef(false)
  const nativeClip = useRef<DOMRectReadOnly | null>(null)
  const refreshQuestionVisibility = useRef<() => void>(() => {})
  const displayedRevision = useRef<number | null>(null)
  const bindFrame = useCallback((node: HTMLIFrameElement | null) => {
    if (frame.current === node) return
    frame.current = node
    questionsViewport.current = null
    displayedRevision.current = null
    visible.current = false
    nativeClip.current = null
  }, [])
  const displayed = useRef(onQuestionsDisplayed)
  displayed.current = onQuestionsDisplayed
  const request = useRef(0)
  const mounted = useRef(true)
  const [quote, setQuote] = useState<QuickStudyQuote | null>(null)
  const [acceptedStudyToken, setAcceptedStudyToken] = useState<string | null>(null)
  const [topics, setTopics] = useState<Array<string>>([])
  const [busy, setBusy] = useState(false)
  const busyRef = useRef(false)
  const [error, setError] = useState<string | null>(null)
  const studyToken = draft.launchedStudyToken ?? acceptedStudyToken
  const launched = Boolean(studyToken)
  const ready = findingsReady
  const failed = draft.studyStatus === 'failed' || draft.reportStatus === 'failed'
  const cancelled = draft.studyStatus === 'cancelled'
  const sampling = launched && !ready && !failed && !cancelled && draft.studyStatus !== 'completed'
  const progressPercent = typeof draft.progressPercent === 'number' && Number.isFinite(draft.progressPercent) && draft.progressPercent >= 0 && draft.progressPercent <= 100 ? draft.progressPercent : null
  const hasCounts = typeof draft.respondentsCompleted === 'number' && typeof draft.numRespondents === 'number' && draft.numRespondents > 0


  function confirmVisibleQuestions() {
    if (visible.current && document.visibilityState !== 'hidden' && frame.current && displayedRevision.current !== null &&
      studyQuestionsVisibleInParent(questionsViewport.current, frame.current.getBoundingClientRect(), window.innerWidth, window.innerHeight, nativeClip.current)) {
      displayed.current?.(draft.id, displayedRevision.current)
    }
  }
  useEffect(() => {
    if (!frame.current || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver((entries) => {
      const entry = entries.find((candidate) => candidate.target === frame.current)
      visible.current = Boolean(entry?.isIntersecting)
      nativeClip.current = entry?.isIntersecting ? entry.intersectionRect : null
      confirmVisibleQuestions()
    })
    const refresh = () => {
      visible.current = false
      nativeClip.current = null
      // A default-threshold observer does not notify for every movement.
      // Reobserve to obtain current native ancestor-scrollport clipping.
      observer.takeRecords()
      if (frame.current) {observer.unobserve(frame.current); observer.observe(frame.current)}
    }
    refreshQuestionVisibility.current = refresh
    refresh()
    document.addEventListener('visibilitychange', refresh)
    window.addEventListener('scroll', refresh, true)
    window.addEventListener('resize', refresh)
    return () => {
      refreshQuestionVisibility.current = () => {}
      observer.disconnect()
      document.removeEventListener('visibilitychange', refresh)
      window.removeEventListener('scroll', refresh, true)
      window.removeEventListener('resize', refresh)
      visible.current = false
      nativeClip.current = null
    }
  }, [draft.id, launched, Boolean(quote), readOnly])

  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false; request.current += 1 }
  }, [])

  useEffect(() => {
    async function onMessage(event: MessageEvent) {
      if (event.origin !== window.location.origin || !frame.current?.contentWindow || event.source !== frame.current.contentWindow) return
      const data = event.data
      if (!data || data.source !== STUDY_EMBED_SOURCE || data.draftToken !== draft.id || !Number.isInteger(data.revision) || data.revision < 0) return
      if (data.type === 'quick-questions-displayed') {
        questionsViewport.current = data.questionsViewport
        displayedRevision.current = data.revision
        refreshQuestionVisibility.current()
        return
      }
      if (data.type !== 'quick-review-requested' || launched || readOnly || busyRef.current) return
      const sequence = ++request.current
      busyRef.current = true
      setBusy(true); setError(null)
      try {
        const saved = await getQuestionnaireDraftApi(draft.id)
        if (!mounted.current || request.current !== sequence) return
        if (saved.presentation !== 'quick' || saved.revision !== data.revision) throw new Error('revision_changed')
        const instrument = saved.artifacts.draft
        const numRespondents = resolvePlannedSampleSize(instrument?.audience?.plannedSampleSize)
        const approved = await quoteQuickStudyDraftApi(draft.id, {expectedRevision: data.revision, numRespondents})
        if (!mounted.current || request.current !== sequence) return
        setTopics((instrument?.researchQuestions ?? []).map((question) => question.title))
        setQuote(approved)
      } catch (failure) {
        if (mounted.current && request.current === sequence) setError(requestError(failure, t('threads.quickStudy.quoteFailed')))
      } finally {
        if (mounted.current && request.current === sequence) {busyRef.current = false; setBusy(false)}
      }
    }
    window.addEventListener('message', onMessage)
    return () => {window.removeEventListener('message', onMessage); request.current += 1; busyRef.current = false}
  }, [draft.id, launched, readOnly, t])

  useEffect(() => {
    // Launch itself advances the draft revision. Reconcile the quote only
    // while it is still awaiting approval, after any in-flight action ends.
    if (!launched && !busy && quote && draft.revision > quote.revision) {setQuote(null); setError(t('threads.quickStudy.questionsChanged'))}
  }, [draft.revision, quote, launched, busy, t])

  useEffect(() => {
    confirmVisibleQuestions()
  }, [draft.id, draft.revision, questionAttentionKey])

  async function approve() {
    if (!quote || busyRef.current || readOnly) return
    busyRef.current = true; setBusy(true); setError(null)
    try {
      const result = await launchQuestionnaireDraftApi(draft.id, {
        expectedRevision: quote.revision, numRespondents: quote.numRespondents,
        quoteToken: quote.quoteToken, idempotencyKey: quote.quoteToken,
      })
      if (mounted.current) {
        setQuote(null)
        setAcceptedStudyToken(result.experimentId)
        try {await onLaunched(result.experimentId)} catch {setError(t('threads.quickStudy.startedRefreshFailed'))}
      }
    } catch (failure) {
      if (mounted.current) setError(requestError(failure, t('threads.quickStudy.launchFailed')))
    } finally {
      if (mounted.current) {busyRef.current = false; setBusy(false)}
    }
  }

  return <section className={`flex ${launched ? 'h-40' : quote ? 'min-h-0' : 'h-[420px]'} flex-col overflow-hidden rounded-xl border border-border bg-background text-foreground`} data-testid="thread-quick-study-card" data-draft-token={draft.id}>
    <header className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-4 py-3">
      <h3 className="min-w-0 truncate text-sm font-medium" title={draft.title ?? undefined}>{draft.title || t('threads.quickStudy.title')}</h3>
      <span className="shrink-0 text-xs text-muted-foreground">{t('threads.quickStudy.title')}</span>
    </header>
    {error ? <div role="alert" className="shrink-0 border-b border-border px-4 py-2 text-sm text-destructive">{error}</div> : null}
    {launched ? <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 px-5 text-center" aria-live="polite">
      {!ready && !failed && !cancelled ? <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden /> : null}
      <p className="text-sm">{t(failed ? 'threads.quickStudy.failed' : cancelled ? 'threads.quickStudy.cancelled' : ready ? 'threads.quickStudy.ready' : draft.studyStatus === 'completed' ? 'threads.quickStudy.processing' : 'threads.quickStudy.running')}</p>
      {sampling && (progressPercent !== null || hasCounts) ? <div className="w-full max-w-sm space-y-1.5" data-testid="thread-study-progress">
        <div className="flex items-center justify-between gap-3 text-xs tabular-nums text-muted-foreground">
          <span>{hasCounts ? t('studyReport.collectedOf', {completed: draft.respondentsCompleted, total: draft.numRespondents}) : null}</span>
          <span>{progressPercent !== null ? `${progressPercent}%` : null}</span>
        </div>
        {progressPercent !== null ? <Progress value={progressPercent} className="h-1.5" /> : null}
      </div> : null}
      {ready ? <Button variant="outline" onClick={() => onOpenResults(studyToken!)}>{t('threads.quickStudy.openResults')}</Button> : null}
    </div> : readOnly ? <div className="flex flex-1 items-center justify-center px-5 text-sm text-muted-foreground">{t('threads.quickStudy.readOnly')}</div> : quote ? <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4">
      <div><p className="text-xs text-muted-foreground">{quote.audienceLabel} · {t('threads.quickStudy.twins', {count: quote.numRespondents})}</p></div>
      <ol className="list-decimal space-y-3 pl-5 text-sm leading-relaxed">{topics.map((topic, index) => <li key={index}>{topic}</li>)}</ol>
      <div className="mt-auto flex items-center justify-between gap-3 border-t border-border pt-3">
        <span className="inline-flex items-center gap-1.5 text-sm font-medium"><CreditIcon className="h-4 w-4" />{t('threads.quickStudy.credits', {count: quote.credits})}</span>
        <div className="flex items-center gap-2"><Button variant="ghost" disabled={busy} onClick={() => {setQuote(null); setError(null)}}>{t('threads.quickStudy.edit')}</Button><Button disabled={busy} onClick={() => void approve()}>{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> : null}{t('threads.quickStudy.approve')}</Button></div>
      </div>
    </div> : <div className="relative min-h-0 flex-1">
      <iframe ref={bindFrame} title={t('threads.quickStudy.editorTitle')} src={studyEmbedDraftPath(draft.id)} className="h-full w-full border-0" data-testid="thread-quick-study-editor" />
      {busy ? <div className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-2 bg-background/95 p-3 text-sm"><Loader2 className="h-4 w-4 animate-spin" aria-hidden />{t('threads.quickStudy.quoting')}</div> : null}
    </div>}
  </section>
}
